import { Typography } from 'antd';
import React, { useCallback, useState } from 'react';
import type { DefaultOptionType } from 'rc-select/lib/Select';
import { ConfigurationSelect } from '@ydistri/ds';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import { styled } from 'styled-components';
import { COLORS, STYLE } from '@ydistri/theme';
import MultiselectSelectedDataModal from './MultiselectSelectedDataModal';
import MultiselectValidationModal from './MultiselectValidationModal';
import useConfigurationMultiselectPaste from './useConfigurationMultiselectPaste';
import type {
  ConfigurationMultiselectProps,
  MultiselectCustomerPartialEntity,
} from './configurationMultiselectTypes';

const WrapperDiv = styled.div`
  width: 100%;
`;

const SelectionActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  min-height: 1rem;
  margin-top: 0.25rem;
`;

const SelectionActionButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.GREY};
  cursor: pointer;
  font-family: inherit;
  font-size: ${STYLE.TEXT_FONT_SIZE.xSmall};
  line-height: 1.2;
  text-decoration: underline;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const ConfigurationMultiselect = <T extends MultiselectCustomerPartialEntity>({
  value,
  onValueChange,
  data,
  dataCheck,
  dataCheckLabel,
  options: providedOptions,
  disabled,
  loading,
  compareAsInt = false,
  placeholder = 'Select or paste data',
  width = '100%',
  size = 'large',
  status,
  popupMatchSelectWidth,
  maxTagCount,
  tagRender,
  notFoundContent,
  showSelectionActions = true,
  resolvePastedData,
  onResolvedPastedData,
  resolutionContextKey,
}: ConfigurationMultiselectProps<T>): React.ReactElement => {
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [selectedDataModalOpen, setSelectedDataModalOpen] = useState<boolean>(false);
  const {
    clearParsedData,
    effectiveData,
    handlePastedData,
    isResolvingPastedData,
    parseResponse,
    setParsedData,
  } = useConfigurationMultiselectPaste({
    compareAsInt,
    data,
    dataCheck,
    onResolvedPastedData,
    onValueChange,
    resolutionContextKey,
    resolvePastedData,
    value,
  });

  const openSelectedDataModal = useCallback(() => {
    setSelectedDataModalOpen(true);
  }, []);

  const closeSelectedDataModal = useCallback(() => {
    setSelectedDataModalOpen(false);
  }, []);

  const clearSelectionHandler = useCallback(() => {
    onValueChange([]);
  }, [onValueChange]);

  const openedValidationModal =
    !!parseResponse &&
    (parseResponse.notRecognized.length > 0 || parseResponse.dataCheckOnlyIds.length > 0);
  const isLoading = loading === true || isResolvingPastedData;

  const options: DefaultOptionType[] =
    providedOptions ??
    effectiveData.map(dataItem => ({
      value: dataItem.id,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- needed
      label: dataItem.label ?? `#${dataItem.customerId ?? dataItem.id} - ${dataItem.title}`,
    }));

  const onChangeHandler = useCallback(
    (input: number[]) => {
      setSearchValue('');
      onValueChange(input);
    },
    [onValueChange],
  );

  const onSearchHandler = useCallback((input: string) => {
    setSearchValue(input);
  }, []);

  const onPasteHandler = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const htmlData = event.clipboardData.getData('text/html');
      const textData = event.clipboardData.getData('text/plain');
      const pastedData = htmlData.trim().length > 0 ? htmlData : textData;
      if (!pastedData) return;

      event.preventDefault();
      setSearchValue('');
      void handlePastedData(pastedData);
    },
    [handlePastedData],
  );

  return (
    <WrapperDiv onPaste={onPasteHandler}>
      <ConfigurationSelect<number[]>
        disabled={disabled}
        mode="multiple"
        tagRender={tagRender}
        placeholder={placeholder}
        value={value}
        onChange={onChangeHandler}
        options={options}
        width={width}
        status={
          status ??
          (value.length === 0 ? ConfigurationFieldStatus.ERROR : ConfigurationFieldStatus.DEFAULT)
        }
        disableOnLoading={false}
        size={size}
        autoClearSearchValue={true}
        searchValue={searchValue}
        onSearch={onSearchHandler}
        loading={isLoading}
        popupMatchSelectWidth={popupMatchSelectWidth}
        maxTagCount={maxTagCount}
        notFoundContent={
          isResolvingPastedData ? (
            <Typography.Text type="secondary" role="status" aria-live="polite">
              Processing pasted data…
            </Typography.Text>
          ) : (
            notFoundContent
          )
        }
      />
      {showSelectionActions && (
        <SelectionActionsRow>
          <SelectionActionButton type="button" onClick={openSelectedDataModal}>
            {value.length} selected
          </SelectionActionButton>
          {value.length > 0 && (
            <SelectionActionButton
              type="button"
              onClick={clearSelectionHandler}
              disabled={disabled}
            >
              Clear selection
            </SelectionActionButton>
          )}
        </SelectionActionsRow>
      )}
      <MultiselectSelectedDataModal
        open={selectedDataModalOpen}
        selectedIds={value}
        data={effectiveData}
        compareAsInt={compareAsInt}
        onClose={closeSelectedDataModal}
      />
      {openedValidationModal && (
        <MultiselectValidationModal
          parsedData={parseResponse}
          selectedIds={value}
          data={effectiveData}
          dataCheck={dataCheck}
          dataCheckLabel={dataCheckLabel}
          compareAsInt={compareAsInt}
          onSubmit={setParsedData}
          onClose={clearParsedData}
        />
      )}
    </WrapperDiv>
  );
};

export default ConfigurationMultiselect;
